import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Button from '../common/Button';
import './ContractDetail.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const VendorContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchContract();
  }, [id]);

  const fetchContract = async () => {
    if (!token) {
      setError('Authentication required');
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/contracts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setContract(data.contract);
        setProgress(data.contract.progress || 0);
      } else {
        setError(data.message || 'Contract not found');
      }
    } catch (error) {
      console.error('Fetch contract error:', error);
      setError('Failed to load contract. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartWork = async () => {
    if (window.confirm('Are you ready to start work on this vessel?')) {
      try {
        const res = await fetch(`${API_BASE}/contracts/${id}/start-work`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          alert('Work started! Update progress as you complete tasks.');
          fetchContract();
        } else {
          alert(data.message || 'Failed to start work');
        }
      } catch (error) {
        alert('Error starting work');
      }
    }
  };

  const handleUpdateProgress = async () => {
    if (progress < 0 || progress > 100) {
      alert('Progress must be between 0 and 100');
      return;
    }
    setUpdating(true);
    try {
      const res = await fetch(`${API_BASE}/contracts/${id}/progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ progress })
      });
      const data = await res.json();
      if (data.success) {
        alert(`Progress updated to ${progress}%`);
        fetchContract();
      } else {
        alert(data.message || 'Failed to update progress');
      }
    } catch (error) {
      alert('Error updating progress');
    } finally {
      setUpdating(false);
    }
  };

  const handleCompleteWork = async () => {
    if (!window.confirm('Mark this work as complete? The ship owner will be asked to confirm completion.')) return;
    try {
      await fetch(`${API_BASE}/contracts/${id}/progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ progress: 100 })
      });
      const res = await fetch(`${API_BASE}/contracts/${id}/complete-work`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        alert('Work marked as complete!');
        fetchContract();
      } else {
        alert(data.message || 'Failed to complete work');
      }
    } catch (error) {
      alert('Error completing work');
    }
  };

  const handleReviseQuotation = () => {
    if (contract && contract.rfqId) {
      navigate(`/service-provider/submit-quotation/${contract.rfqId}`, {
        state: { revise: true, contractId: contract._id }
      });
    } else {
      alert('Cannot revise quotation: RFQ ID not found.');
    }
  };

  const handleMessageOwner = () => {
    const ownerId = contract?.ownerId?._id || contract?.ownerId;
    if (ownerId) {
      navigate(`/messages/${ownerId}`);
    }
  };

  const getStatusDisplay = () => {
    const statusMap = {
      pending_payment: {
        text: 'Active Contract',
        color: '#10b981',
        description: 'The contract is active. You can start work whenever you are ready.'
      },
      payment_uploaded: {
        text: 'Active Contract',
        color: '#10b981',
        description: 'The contract is active. You can start work whenever you are ready.'
      },
      payment_verified: {
        text: 'Ready to Start',
        color: '#10b981',
        description: 'The contract is active. You can start work now.'
      },
      work_in_progress: {
        text: 'Work In Progress',
        color: '#3b82f6',
        description: 'Update progress as you complete tasks.'
      },
      work_completed: {
        text: 'Work Completed',
        color: '#10b981',
        description: 'Waiting for the ship owner to confirm completion.'
      },
      payment_released: {
        text: 'Contract Completed',
        color: '#10b981',
        description: 'The contract has been completed.'
      },
      cancelled: {
        text: 'Cancelled',
        color: '#ef4444',
        description: 'This contract has been cancelled.'
      }
    };
    return statusMap[contract?.status] || {
      text: contract?.status || 'Unknown',
      color: '#6b6b6b',
      description: ''
    };
  };

  if (loading) return <div className="loading-spinner">Loading contract...</div>;
  if (error) return (
    <div className="error-message" style={{ textAlign: 'center', padding: '40px' }}>
      {error}<br/>
      <button onClick={() => navigate(-1)} style={{ marginTop: '16px' }}>Go Back</button>
    </div>
  );
  if (!contract) return <div className="error-message">Contract not found</div>;

  const status = getStatusDisplay();

  return (
    <div className="vendor-contract-detail">
      <button className="back-btn" onClick={() => navigate('/service-provider/active-work')}>
        <i className="fas fa-arrow-left"></i> Back to My Work
      </button>

      <div className="contract-card">
        <div className="contract-header">
          <h1>{contract.vesselName}</h1>
          <div className="contract-meta">
            <span className="contract-id">Contract: {contract._id?.slice(-8)}</span>
            <span
              className="contract-status"
              style={{
                background: `${status.color}15`,
                color: status.color,
                padding: '6px 14px',
                borderRadius: '30px'
              }}
            >
              {status.text}
            </span>
          </div>
        </div>

        <div className="contract-body">
          <div className="info-grid">
            <div><span className="label">Ship Owner</span><span className="value">{contract.ownerName || 'Owner'}</span></div>
            <div><span className="label">Service Type</span><span className="value">{contract.serviceType}</span></div>
            <div><span className="label">Contract Amount</span><span className="value amount-highlight">${contract.originalAmount?.toLocaleString()}</span></div>
            <div><span className="label">Total Contract Value</span><span className="value">${contract.totalAmount?.toLocaleString()}</span></div>
            <div><span className="label">Status</span><span className="value" style={{ color: status.color }}>{status.description}</span></div>
          </div>

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <Button variant="secondary" onClick={handleMessageOwner} icon="fa-envelope">
              Message Owner
            </Button>
          </div>

          {(contract.status === 'payment_verified' ||
            contract.status === 'pending_payment' ||
            contract.status === 'payment_uploaded') && (
            <div style={{ marginTop: '24px', textAlign: 'center', padding: '20px', background: 'rgba(24,203,150,0.1)', borderRadius: '12px' }}>
              <i className="fas fa-play-circle" style={{ fontSize: '48px', color: '#18cb96' }}></i>
              <h3 style={{ marginBottom: '8px' }}>Ready to Start?</h3>
              <p style={{ marginBottom: '16px', color: '#aaa' }}>Click below to begin work on this contract.</p>
              <Button variant="primary" onClick={handleStartWork} icon="fa-play">Start Work</Button>
              <Button variant="outline" onClick={handleReviseQuotation} icon="fa-edit" style={{ marginLeft: '12px' }}>
                Revise Quotation
              </Button>
            </div>
          )}

          {contract.status === 'work_in_progress' && (
            <div className="progress-section" style={{ marginTop: '24px' }}>
              <div className="progress-header">
                <span>Work Progress</span>
                <span className="progress-percent">{progress}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%`, background: 'var(--primary)' }}></div>
              </div>
              <div style={{ marginTop: '16px' }}>
                <label>Update Progress (%)</label>
                <div style={{ display: 'flex', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={progress}
                    onChange={e => setProgress(parseInt(e.target.value))}
                    style={{ flex: 1 }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={progress}
                    onChange={e => setProgress(parseInt(e.target.value))}
                    style={{ width: '70px', textAlign: 'center' }}
                  />
                  <Button variant="secondary" onClick={handleUpdateProgress} disabled={updating} size="small">
                    {updating ? 'Updating...' : 'Update'}
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button variant="primary" onClick={handleCompleteWork} icon="fa-check-double">
                  Mark Work Complete
                </Button>
                <Button variant="outline" onClick={handleReviseQuotation} icon="fa-edit">
                  Revise Quotation
                </Button>
              </div>
            </div>
          )}

          {contract.status === 'work_completed' && (
            <div
              className="completion-message"
              style={{
                marginTop: '24px',
                padding: '20px',
                background: 'rgba(16,185,129,0.1)',
                borderRadius: '12px',
                textAlign: 'center'
              }}
            >
              <i className="fas fa-check-circle" style={{ fontSize: '32px', color: '#10b981', marginBottom: '8px' }}></i>
              <p>Work marked complete – waiting for owner confirmation.</p>
            </div>
          )}

          {contract.status === 'payment_released' && (
            <div
              style={{
                marginTop: '24px',
                padding: '20px',
                background: 'rgba(24,203,150,0.1)',
                borderRadius: '12px',
                textAlign: 'center'
              }}
            >
              <i className="fas fa-check-double" style={{ fontSize: '32px', color: '#18cb96', marginBottom: '8px' }}></i>
              <p>Contract completed successfully!</p>
            </div>
          )}

          {contract.status === 'cancelled' && (
            <div
              style={{
                marginTop: '24px',
                padding: '20px',
                background: 'rgba(239,68,68,0.1)',
                borderRadius: '12px',
                textAlign: 'center'
              }}
            >
              <i className="fas fa-ban" style={{ fontSize: '32px', color: '#ef4444', marginBottom: '8px' }}></i>
              <p>This contract has been cancelled.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorContractDetail;